"use client";

/**
 * useDepositWallet — Phase 1 enable-trading flow for new Polymarket users
 * (rolled out 2026-05-04). Mirrors `useSafeWallet` but produces a Polymarket
 * deposit wallet (deterministic ERC-1967 proxy) instead of a Gnosis Safe.
 *
 * Flow:
 *   1. Derive deposit wallet address (CREATE2 of owner EOA + factory).
 *   2. Deploy via relayer `WALLET-CREATE` (no user signature).
 *   3. Build approvals batch (pUSD/CTF spenders + USDC.e/USDC native onramp
 *      allowances + optional FrenLinks/FeeCollector).
 *   4. Submit a relayer `WALLET` batch — owner EOA signs the EIP-712 Batch
 *      payload through Privy TEE; relayer pays gas.
 *   5. Derive POLY_1271 CLOB credentials (key/secret/passphrase).
 *   6. POST /api/trading/enable with { eoaAddress, depositWalletAddress, credentials }.
 *
 * Reference: docs.polymarket.com/trading/deposit-wallet-migration
 */

import { useCallback, useRef } from "react";
import { useWallets, getAccessToken } from "@privy-io/react-auth";
import { polygon as polygonBase } from "viem/chains";

const CHAIN_ID = 137;

// The RelayClient builds its OWN viem publicClient internally with
// `transport: http()` (no URL), which falls back to the chain's default RPC.
// viem's stock `polygon` chain hardcodes https://polygon.drpc.org — a free
// public endpoint that rate-limits/flakes and aborted the enable flow with
// "HTTP request failed" (incident 2026-06-03). We override the chain's RPC to
// our SAME-ORIGIN proxy (/api/rpc/polygon → backend → paid Infura server-side):
// no CORS, and no RPC key in the browser bundle.
//
// We deliberately IGNORE NEXT_PUBLIC_POLYGON_RPC_URL. That env was set to a raw
// Alchemy URL, and giving it precedence (first version of this fix) sent the
// browser straight to Alchemy → "Failed to fetch" (Alchemy blocks cross-origin
// browser requests / the key is origin-restricted) AND leaked the key into the
// bundle. NEXT_PUBLIC_* is build-time-inlined, so a keyed RPC there can never be
// safe client-side — the proxy is the only correct path.
function buildPolygonChain() {
  const origin =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://www.frenflow.com";
  const rpc = `${origin}/api/rpc/polygon`;
  return {
    ...polygonBase,
    rpcUrls: {
      default: { http: [rpc] },
      public: { http: [rpc] },
    },
  };
}
// Deposit-wallet flows use a separate relayer URL during the preprod testing
// window. Falls back to the generic Polymarket relayer URL, then to prod.
// Set NEXT_PUBLIC_POLYMARKET_DEPOSIT_WALLET_RELAYER_URL=https://relayer-v2-preprod-int.polymarket.com/
// in dev to test against the preprod relayer (which accepts WALLET-CREATE
// before the 2026-05-04 production rollout).
const RELAYER_URL =
  process.env.NEXT_PUBLIC_POLYMARKET_DEPOSIT_WALLET_RELAYER_URL ||
  process.env.NEXT_PUBLIC_POLYMARKET_RELAYER_URL ||
  "https://relayer-v2.polymarket.com";
const CLOB_URL = process.env.NEXT_PUBLIC_POLYMARKET_CLOB_URL || "https://clob.polymarket.com";

// ── Relayer resilience (2026-06-19) ─────────────────────────────────────────
// The Polymarket relayer SDK (@polymarket/builder-relayer-client) makes
// browser→relayer calls with NO timeout and NO retry. A flaky user network or a
// momentary relayer hiccup surfaces as a dead `{"error":"connection error"}`
// (axios received no response) and the whole enable flow dies — the user can't
// fund. Observed in prod: a handful of users hammering 10-16 MANUAL retries each
// because the flow self-recovered from nothing. We add (a) a per-call timeout —
// the SDK has none, so a hung socket hangs forever — and (b) bounded backoff,
// retrying ONLY genuinely transient failures. Real errors ("call blocked",
// validation, etc.) throw immediately so we never mask a real bug.
const RELAY_CALL_TIMEOUT_MS = 30_000;
const RELAY_BACKOFF_MS = [1_500, 3_000, 6_000];

/**
 * Flatten a relayer error into one searchable string. The SDK throws THREE
 * different shapes: a real `Error` (has `.message`), an axios-ish envelope
 * `{ error: "request error", status, data: { error: "<real reason>" } }`, and
 * a bare string. Reading only `.message ?? .data.error` misses the reason
 * whenever a shape carries both, so we concatenate every candidate instead —
 * the retry predicates below match on substrings, never on exact equality.
 */
function relayErrorText(err: any): string {
  if (typeof err === "string") return err;
  const parts = [err?.message, err?.data?.error, err?.error, err?.statusText].filter(
    (p) => typeof p === "string" && p.length > 0,
  );
  if (parts.length === 0) {
    try {
      return JSON.stringify(err ?? "");
    } catch {
      return String(err ?? "");
    }
  }
  return parts.join(" | ");
}

function isTransientRelayError(err: any): boolean {
  const msg = relayErrorText(err);
  return /connection error|relay timeout|network error|fetch failed|ECONNRESET|ETIMEDOUT|ENOTFOUND|socket hang up/i.test(
    msg,
  );
}

/**
 * Run a single relayer call with a timeout + backoff retry on transient
 * failures. `alsoRetryIf` lets a caller add its own retryable condition (e.g.
 * the registry-indexing race). On exhaustion of a transient failure we throw a
 * clear, actionable message instead of the raw `{"error":"connection error"}`.
 * NOTE: do NOT wrap long-poll calls (pollUntilState) — the timeout is sized for
 * a single request, not a multi-minute poll.
 */
async function withRelayRetry<T>(
  fn: () => Promise<T>,
  opts: {
    progress?: (m: string) => void;
    backoff?: number[];
    alsoRetryIf?: (msg: string) => boolean;
  } = {},
): Promise<T> {
  const backoff = opts.backoff ?? RELAY_BACKOFF_MS;
  let lastErr: any;
  for (let attempt = 0; attempt <= backoff.length; attempt++) {
    try {
      return await Promise.race([
        fn(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("relay timeout")), RELAY_CALL_TIMEOUT_MS),
        ),
      ]);
    } catch (err: any) {
      lastErr = err;
      const msg = relayErrorText(err);
      const retryable = isTransientRelayError(err) || (opts.alsoRetryIf?.(msg) ?? false);
      if (!retryable || attempt >= backoff.length) break;
      const wait = backoff[attempt];
      opts.progress?.(`Network issue reaching the relayer — retrying in ${Math.round(wait / 1000)}s…`);
      await new Promise((r) => setTimeout(r, wait));
    }
  }
  if (isTransientRelayError(lastErr)) {
    throw new Error("Couldn't reach the trading relayer. Check your connection and try again.");
  }
  throw lastErr;
}

// Contract addresses — same set as useSafeWallet but signed FROM the deposit
// wallet itself rather than the Safe. Polygon mainnet (chainId 137).
const USDC_E = "0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174";
const USDC_NATIVE = "0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359";
const NEG_RISK_ADAPTER = "0xd91E80cF2E7be2e162c6513ceD06f1dD0dA35296";
const CTF = "0x4D97DCd97eC945f40cF65F87097ACe5EA0476045";
const CTF_EXCHANGE_V2 = "0xE111180000d2663C0091e4f400237545B87B996B";
const NEG_RISK_EXCHANGE_V2 = "0xe2222d279d744050d28e00520010520000310F59";
const PUSD = "0xC011a7E12a19f7B1f670d46F03B03f3342E82DFB";
const COLLATERAL_ONRAMP = "0x93070a847efEf7F70739046A929D47a521F5B8ee";
const COLLATERAL_OFFRAMP = "0x2957922Eb93258b93368531d39fAcCA3B4dC5854";
// Adapter contracts that wrap CTF.redeemPositions and convert the USDC.e
// payout into pUSD in a single tx. The deposit wallet must approve them
// as ERC-1155 operators in the WALLET-CREATE batch so they can pull
// outcome tokens to burn during redeem.
const CTF_COLLATERAL_ADAPTER = "0xAdA100Db00Ca00073811820692005400218FcE1f";
const NEG_RISK_CTF_COLLATERAL_ADAPTER = "0xadA2005600Dec949baf300f4C6120000bDB6eAab";
const FEE_COLLECTOR =
  process.env.NEXT_PUBLIC_FEE_COLLECTOR_ADDRESS || "0x95e47CBC5c4D9434412AF44Ade02B33613EDb787";

// MaxUint256 (unlimited). Polymarket's deposit-wallet relayer REQUIRES the
// approve to the V2 exchanges/adapters to be EXACTLY MaxUint256 and rejects any
// capped amount with "approve to exchange … must be MaxUint256" — which blocked
// Enable Trading (Setup) for every new user. Verified in prod logs 2026-06-09:
// `call[1] blocked: approve to exchange 0xE111…996B must be MaxUint256`, where
// call[1] is the CTF_EXCHANGE_V2 approve below. The old 10M cap ("10000000000000")
// is what tripped it; the relayer's allowed-spender list is the real guard, so
// capping the amount added no security and broke setup. Must match the backend
// (prepare-trading.ts / approvals.ts) which already use MaxUint256.
// = 2^256 − 1, written as a decimal literal (BigInt `2n` literals need ES2020).
const MAX_APPROVAL = "115792089237316195423570985008687907853269984665640564039457584007913129639935";

// ERC20.approve(address,uint256) calldata — selector 0x095ea7b3.
function encodeApprove(spender: string, amount: string): string {
  const selector = "0x095ea7b3";
  const paddedSpender = spender.toLowerCase().replace("0x", "").padStart(64, "0");
  const paddedAmount = BigInt(amount).toString(16).padStart(64, "0");
  return `${selector}${paddedSpender}${paddedAmount}`;
}

// ERC1155.setApprovalForAll(address,bool) — selector 0xa22cb465.
function encodeSetApprovalForAll(operator: string): string {
  const selector = "0xa22cb465";
  const paddedOperator = operator.toLowerCase().replace("0x", "").padStart(64, "0");
  const paddedTrue = "1".padStart(64, "0");
  return `${selector}${paddedOperator}${paddedTrue}`;
}

interface DepositWalletResult {
  eoaAddress: string;
  depositWalletAddress: string;
  credentials: { apiKey: string; secret: string; passphrase: string };
}

export interface UseDepositWalletReturn {
  enableViaDepositWallet: (
    onProgress?: (msg: string) => void,
  ) => Promise<DepositWalletResult>;
}

export function useDepositWallet(): UseDepositWalletReturn {
  const { wallets } = useWallets();
  const sdkCacheRef = useRef<any>(null);

  // Lazy-load Polymarket SDKs (heavy — only needed during enable).
  const loadSDKs = useCallback(async () => {
    if (sdkCacheRef.current) return sdkCacheRef.current;

    const [{ RelayClient }, { BuilderConfig }] = await Promise.all([
      import("@polymarket/builder-relayer-client"),
      import("@polymarket/builder-signing-sdk"),
    ]);

    sdkCacheRef.current = { RelayClient, BuilderConfig };
    return sdkCacheRef.current;
  }, []);

  const enableViaDepositWallet = useCallback(
    async (onProgress?: (msg: string) => void): Promise<DepositWalletResult> => {
      const progress = (msg: string) => onProgress?.(msg);

      const embeddedWallet = wallets.find((w) => w.walletClientType === "privy");
      if (!embeddedWallet) throw new Error("No embedded wallet found");

      const eoaAddress = embeddedWallet.address;
      progress("Loading SDKs...");

      const provider = await embeddedWallet.getEthereumProvider();
      const { ethers } = await import("ethers");
      const ethersProvider = new ethers.providers.Web3Provider(provider as any);
      const signer = ethersProvider.getSigner();

      const { RelayClient, BuilderConfig } = await loadSDKs();

      // Builder credentials gate the relayer. Backend hands them back to
      // logged-in users only — the same endpoint already used by useSafeWallet.
      const accessToken = await getAccessToken();
      const authHeaders: Record<string, string> = {};
      if (accessToken) authHeaders["Authorization"] = `Bearer ${accessToken}`;

      const configResp = await fetch("/api/polymarket/builder-credentials", {
        headers: authHeaders,
      });
      if (!configResp.ok) throw new Error("Failed to fetch builder credentials");
      const builderCreds = await configResp.json();

      const builderConfig = new BuilderConfig({
        localBuilderCreds: {
          key: builderCreds.apiKey,
          secret: builderCreds.secret,
          passphrase: builderCreds.passphrase,
        },
      });

      const relayClient = new RelayClient(
        RELAYER_URL,
        CHAIN_ID,
        signer as any,
        builderConfig as any,
        // relayTxType: keep the SDK default (unchanged behaviour).
        undefined,
        // options.chain: route the SDK's internal publicClient reads
        // (deriveDepositWalletAddress → BEACON() + getCode) through our RPC
        // instead of viem's default drpc.org.
        { chain: buildPolygonChain() } as any,
      );

      // ===== STEP 1: Derive + deploy deposit wallet =====
      // Each relayer call is wrapped with timeout + transient-retry so a flaky
      // network / momentary relayer hiccup doesn't kill the whole enable flow.
      progress("Computing deposit wallet address...");
      const depositWalletAddress: string = await withRelayRetry(
        () => relayClient.deriveDepositWalletAddress(),
        { progress },
      );
      progress(`Deposit wallet: ${depositWalletAddress.slice(0, 10)}...`);

      const isDeployed = await withRelayRetry(
        () => relayClient.getDeployed(depositWalletAddress, "WALLET"),
        { progress },
      );
      if (!isDeployed) {
        progress("Deploying deposit wallet...");
        // CREATE2-deterministic address → a retried deploy targets the same
        // wallet (idempotent); pollUntilState below confirms the real state.
        const deployResp = await withRelayRetry<{ transactionID: string }>(
          () => relayClient.deployDepositWallet(),
          { progress },
        );
        const result = await relayClient.pollUntilState(
          deployResp.transactionID,
          [
            "STATE_MINED",
            "STATE_CONFIRMED",
            "STATE_EXECUTED",
            "MINED",
            "CONFIRMED",
            "EXECUTED",
          ],
          "STATE_FAILED",
          30,
          3000,
        );
        if (!result || result.state === "STATE_FAILED" || result.state === "STATE_INVALID") {
          throw new Error(
            `Deposit wallet deploy failed (${result?.state ?? "no response"})`,
          );
        }
        progress("Deposit wallet deployed!");
      } else {
        progress("Deposit wallet already deployed");
      }

      // ===== STEP 2: Submit WALLET batch with all approvals =====
      progress("Setting up approvals...");

      // Approvals come FROM the deposit wallet (per docs). Each call hits
      // a different token / spender.
      const calls: Array<{ target: string; value: string; data: string }> = [
        // pUSD spending allowances (CLOB V2 debits pUSD on every fill).
        { target: PUSD, value: "0", data: encodeApprove(CTF, MAX_APPROVAL) },
        { target: PUSD, value: "0", data: encodeApprove(CTF_EXCHANGE_V2, MAX_APPROVAL) },
        { target: PUSD, value: "0", data: encodeApprove(NEG_RISK_ADAPTER, MAX_APPROVAL) },
        { target: PUSD, value: "0", data: encodeApprove(NEG_RISK_EXCHANGE_V2, MAX_APPROVAL) },
        { target: PUSD, value: "0", data: encodeApprove(COLLATERAL_OFFRAMP, MAX_APPROVAL) },
        // CTF (ERC-1155) operator permissions. Required for fills (exchanges
        // move outcome tokens), neg-risk redeems, and the collateral-adapter
        // redeem path (adapter pulls outcome tokens to burn + pay pUSD).
        { target: CTF, value: "0", data: encodeSetApprovalForAll(CTF_EXCHANGE_V2) },
        { target: CTF, value: "0", data: encodeSetApprovalForAll(NEG_RISK_EXCHANGE_V2) },
        { target: CTF, value: "0", data: encodeSetApprovalForAll(NEG_RISK_ADAPTER) },
        { target: CTF, value: "0", data: encodeSetApprovalForAll(CTF_COLLATERAL_ADAPTER) },
        { target: CTF, value: "0", data: encodeSetApprovalForAll(NEG_RISK_CTF_COLLATERAL_ADAPTER) },
        // USDC.e + USDC native → CollateralOnramp for self-wrap to pUSD.
        { target: USDC_E, value: "0", data: encodeApprove(COLLATERAL_ONRAMP, MAX_APPROVAL) },
        { target: USDC_NATIVE, value: "0", data: encodeApprove(COLLATERAL_ONRAMP, MAX_APPROVAL) },
        // FeeCollector approve disabled (2026-05-04): Polymarket's deposit-wallet
        // relayer maintains an allowed-spenders list and only Polymarket-owned
        // contracts pass it. Approving our FeeCollector
        // (0x95e47CBC5c4D9434412AF44Ade02B33613EDb787) returns:
        //   "call blocked: approve spender X is not in the allowed list"
        // Pending whitelist request to PM. Until then, deposit-wallet users
        // cannot pre-approve the FeeCollector — fees on their trades fail to
        // collect on-chain. Restore once whitelisted.
        // { target: PUSD, value: "0", data: encodeApprove(FEE_COLLECTOR, MAX_APPROVAL) },
      ];

      // FrenLinks setApprovalForAll disabled (2026-05-04): Polymarket's
      // deposit-wallet relayer rejects setApprovalForAll to non-whitelisted
      // operators ("call blocked: setApprovalForAll operator X is not in
      // the allowed list"). Same allow-list that blocks the FeeCollector.
      // Until PM whitelists FrenLinks (0x88e0A862…), deposit-wallet users
      // can't pre-approve it — share-escrow flow won't work for them.
      // Restore once whitelisted.
      // const frenlinksAddress = process.env.NEXT_PUBLIC_FRENLINKS_CONTRACT_ADDRESS;
      // if (frenlinksAddress) {
      //   calls.push({
      //     target: CTF,
      //     value: "0",
      //     data: encodeSetApprovalForAll(frenlinksAddress),
      //   });
      // }

      // 15 min deadline. Polymarket's relayer rejects shorter ones with
      // "deadline too soon" — leaving headroom for the Privy TEE round-trip
      // (the EIP-712 signature can take a few seconds) plus relayer queue.
      const deadline = Math.floor(Date.now() / 1000 + 900).toString();

      // Retry the batch with backoff for TWO transient failure modes:
      //  1. Registry race — WALLET-CREATE mines on-chain but Polymarket's relayer
      //     indexer needs a few seconds to process the WalletDeployed event into
      //     its internal registry. Submitting in that window returns "wallet
      //     registry validation failed: wallet ... is not registered" OR
      //     "deposit wallet 0x... is not deployed" (400) even though the wallet
      //     IS deployed (verifiable via getDeployed). Catches up within 5-15s.
      //     BOTH wordings must be retried: only "not registered" was covered
      //     until 2026-08-09, so every user who hit the "not deployed" variant
      //     aborted on the first attempt and fell through to the generic
      //     "Failed to create CLOB credentials" dead end (35 users since
      //     mid-July, 4 of them never recovered — CloutZzy 2026-07-24).
      //  2. Connection error / relayer hiccup (handled by withRelayRetry's
      //     built-in transient check). The approval batch is idempotent
      //     (MAX_APPROVAL twice = no-op), so a retried submit is safe.
      // Longer backoff than reads since the indexer race needs real seconds.
      const batchResp = await withRelayRetry<{ transactionID: string }>(
        () => relayClient.executeDepositWalletBatch(calls, depositWalletAddress, deadline),
        {
          progress,
          backoff: [3000, 6000, 10000],
          alsoRetryIf: (msg) => /not registered|not deployed/i.test(msg),
        },
      );
      progress("Waiting for approvals confirmation...");
      const batchResult = await relayClient.pollUntilState(
        batchResp.transactionID,
        [
          "STATE_MINED",
          "STATE_CONFIRMED",
          "STATE_EXECUTED",
          "MINED",
          "CONFIRMED",
          "EXECUTED",
        ],
        "STATE_FAILED",
        60,
        2000,
      );
      if (!batchResult || batchResult.state === "STATE_FAILED" || batchResult.state === "STATE_INVALID") {
        throw new Error(`Approvals batch failed (${batchResult?.state ?? "no response"})`);
      }
      progress("All approvals confirmed!");

      // ===== STEP 3: Derive CLOB API credentials =====
      // L1 sign uses the EOA — the deposit wallet's owner. POLY_1271 only kicks
      // in for orders; credential derivation is still keyed off the EOA.
      progress("Creating trading credentials...");

      const timestamp = Math.floor(Date.now() / 1000).toString();
      const clobAuthDomain = { name: "ClobAuthDomain", version: "1", chainId: CHAIN_ID };
      const clobAuthTypes = {
        ClobAuth: [
          { name: "address", type: "address" },
          { name: "timestamp", type: "string" },
          { name: "nonce", type: "uint256" },
          { name: "message", type: "string" },
        ],
      };
      const clobAuthMessage = {
        address: eoaAddress,
        timestamp,
        nonce: 0,
        message: "This message attests that I control the given wallet",
      };

      const signature = await signer._signTypedData(clobAuthDomain, clobAuthTypes, clobAuthMessage);

      const clobHeaders: Record<string, string> = {
        "Content-Type": "application/json",
        POLY_ADDRESS: eoaAddress,
        POLY_SIGNATURE: signature,
        POLY_TIMESTAMP: timestamp,
        POLY_NONCE: "0",
      };

      // Best-effort delete of any stale credentials, then create fresh ones.
      try {
        await fetch(`${CLOB_URL}/auth/api-keys`, { method: "DELETE", headers: clobHeaders });
      } catch {
        // Non-critical — proceed to create.
      }

      const timestamp2 = Math.floor(Date.now() / 1000).toString();
      const signature2 = await signer._signTypedData(
        clobAuthDomain,
        clobAuthTypes,
        { ...clobAuthMessage, timestamp: timestamp2 },
      );
      const clobHeaders2: Record<string, string> = {
        "Content-Type": "application/json",
        POLY_ADDRESS: eoaAddress,
        POLY_SIGNATURE: signature2,
        POLY_TIMESTAMP: timestamp2,
        POLY_NONCE: "0",
      };

      let credentials: { apiKey: string; secret: string; passphrase: string };
      const createResp = await fetch(`${CLOB_URL}/auth/api-key`, {
        method: "POST",
        headers: clobHeaders2,
      });
      if (createResp.ok) {
        credentials = await createResp.json();
      } else {
        const deriveResp = await fetch(`${CLOB_URL}/auth/derive-api-key`, {
          headers: clobHeaders2,
        });
        if (!deriveResp.ok) {
          // Carry the CLOB's own reason into the message. The bare
          // "Failed to create CLOB credentials" string is what lands in
          // onboarding_events, and with no status/body it says nothing about
          // WHY setup died (CloutZzy 2026-07-24: three identical rows, zero
          // signal). Body is truncated — it's a short JSON error, not a page.
          const detail = await deriveResp.text().catch(() => "");
          throw new Error(
            `Failed to create CLOB credentials (create ${createResp.status}, derive ${deriveResp.status}${
              detail ? `: ${detail.slice(0, 200)}` : ""
            })`,
          );
        }
        credentials = await deriveResp.json();
      }

      progress("Trading credentials ready!");

      return { eoaAddress, depositWalletAddress, credentials };
    },
    [wallets, loadSDKs],
  );

  return { enableViaDepositWallet };
}
